/**
 * Passwordless email — magic-link.
 *
 * Two-state UX in one popup:
 *
 *   1. User submits email → Firebase emails them a sign-in link
 *      pointed back at THIS auth page (auth.horuswallet.com/?…).
 *   2. User clicks the link from their email. The link target also
 *      lands on this auth page; we detect we're a follow-up via
 *      `isSignInWithEmailLink(window.location.href)`, complete the
 *      sign-in, and `postMessage` the resulting token back to the
 *      original opener (if it's still around).
 *
 * Because step 2 may run in a NEW tab (user clicks link in mail app),
 * the original popup may be closed by the time the user lands here.
 * In that case the auth page falls back to a "Sign-in complete; you
 * can close this tab" UI and stores the token in `localStorage` keyed
 * by `state` so the SDK in the partner site can pick it up via a
 * cross-tab BroadcastChannel — handled by the SDK in v0.2.
 *
 * For v0.1 we surface the success message and rely on the user
 * staying in the original tab (most email clients open links in the
 * same browser process).
 */

import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import { getFirebaseAuth } from '../firebase';
import { PostMessageContext } from '../postmessage';
import {
  el,
  mount,
  postCancel,
  postError,
  postReady,
  postSuccess,
  humanizeFirebaseError,
  setBusy,
  getQueryParam,
} from '../util';

const STORAGE_EMAIL_KEY = 'horus.auth.emailForSignIn';

export async function renderEmailLinkFlow(ctx: PostMessageContext): Promise<void> {
  const auth = getFirebaseAuth();

  // Step 2 — link click. If the URL looks like a sign-in link, finish.
  if (isSignInWithEmailLink(auth, window.location.href)) {
    await completeSignIn(ctx);
    return;
  }

  // Step 1 — request the link.
  const titleEl = el('h1', {}, ['Sign in with a link']);
  const subtitleEl = el('p', { class: 'subtitle' }, [
    'We’ll email you a one-time sign-in link.',
  ]);
  const errorBox = el('p', { 'data-error': 'true', class: 'error', style: 'display:none' });

  const emailInput = el('input', {
    type: 'email',
    autocomplete: 'email',
    placeholder: 'you@example.com',
    required: true,
  }) as HTMLInputElement;
  const prefilled = getQueryParam('email');
  if (prefilled) emailInput.value = prefilled;

  const submit = el('button', { class: 'primary', type: 'submit' }, [
    'Email me a link',
  ]) as HTMLButtonElement;
  const cancel = el('button', { type: 'button' }, ['Cancel']);
  cancel.addEventListener('click', () => postCancel(ctx));

  const form = el('form', {}, [
    el('label', { for: 'email' }, ['Email']),
    emailInput,
    submit,
    cancel,
  ]);

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    errorBox.style.display = 'none';
    setBusy(true);
    try {
      // continueUrl MUST be on the PARTNER's origin, not auth-page's.
      // When the user clicks the email link, their mail client opens
      // it in a new browser tab — that new tab has no `window.opener`
      // back to the partner site if we returned them here. Pointing
      // continueUrl at the partner origin (with `?email=` preserved)
      // lets the partner's `<HorusProvider>` detect the magic-link
      // params on mount and complete the sign-in in their own tab.
      //
      // Embed the email in the continueUrl too — Firebase's verify
      // step requires the same email that was used to send. Keeps
      // the link self-contained and survives users clicking from a
      // different browser (where localStorage wouldn't have it).
      const continueUrl = buildPartnerReturnUrl(ctx.partnerOrigin, emailInput.value);
      await sendSignInLinkToEmail(auth, emailInput.value, {
        url: continueUrl,
        handleCodeInApp: true,
      });
      // Mirror to localStorage as a fallback for the auth-page's own
      // verify path (in case the link does come back here, e.g.
      // partner site is down at click-time).
      window.localStorage.setItem(STORAGE_EMAIL_KEY, emailInput.value);
      mount(
        el('div', {}, [
          el('h1', {}, ['Check your email']),
          el('p', { class: 'subtitle' }, [
            `We sent a sign-in link to ${emailInput.value}. Click it from this device to finish signing in.`,
          ]),
          el('p', { class: 'subtitle' }, [
            'You can close this window — the link will bring you back to the app.',
          ]),
        ])
      );
    } catch (err) {
      const { code, message } = humanizeFirebaseError(err);
      errorBox.textContent = message;
      errorBox.style.display = 'block';
      postError(ctx, code, message);
    } finally {
      setBusy(false);
    }
  });

  mount(el('div', {}, [titleEl, subtitleEl, errorBox, form]));
  postReady(ctx);
}

async function completeSignIn(ctx: PostMessageContext): Promise<void> {
  const auth = getFirebaseAuth();
  let email = window.localStorage.getItem(STORAGE_EMAIL_KEY) ?? '';
  // Edge case: link clicked in a different browser session — prompt
  // the user to retype the email so Firebase can verify the link.
  if (!email) {
    email = window.prompt('Enter the email you used to request the sign-in link') ?? '';
    if (!email) {
      postError(ctx, 'auth/missing-email', 'Sign-in link is missing the email address.');
      return;
    }
  }
  try {
    setBusy(true);
    const result = await signInWithEmailLink(auth, email, window.location.href);
    window.localStorage.removeItem(STORAGE_EMAIL_KEY);
    await postSuccess(ctx, result.user, 'emailLink');
  } catch (err) {
    const { code, message } = humanizeFirebaseError(err);
    mount(
      el('div', {}, [
        el('h1', {}, ['Sign-in link couldn’t be verified']),
        el('p', { class: 'error', 'data-error': 'true' }, [message]),
      ])
    );
    postError(ctx, code, message);
  } finally {
    setBusy(false);
  }
}

/**
 * Compose the URL Firebase will append `?apiKey=…&oobCode=…&mode=signIn`
 * to when the user clicks the email link. Must be on the partner's
 * origin so the link returns the user to their site (where the
 * `<HorusProvider>` auto-detect picks up the params and completes
 * the sign-in). Email is preserved in the URL so Firebase's verify
 * step has the matching address even if the user clicks from a
 * different browser than the one that requested the link.
 *
 * Note Firebase requires the continueUrl to be on the project's
 * Authorized Domains list. `auth.horuswallet.com`, `localhost`, and
 * partner production domains all need to be there. (Firebase Console
 * → Authentication → Settings → Authorized domains.)
 */
function buildPartnerReturnUrl(partnerOrigin: string, email: string): string {
  const u = new URL('/', partnerOrigin);
  u.searchParams.set('email', email);
  return u.toString();
}
